
import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthRepository } from '../../data/repositories/AuthRepository';

type LoginLocationState = {
  from?: unknown;
};

function sanitizeRedirectPath(value: unknown): string {
  if (typeof value !== 'string') return '/dashboard';
  if (!value.startsWith('/') || value.startsWith('//')) return '/dashboard';
  if (value.startsWith('/login')) return '/dashboard';

  return value;
}

export default function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const authRepository = new AuthRepository();
  const redirectTo = useMemo(
    () => {
      const stateRedirect = (location.state as LoginLocationState | null)?.from;
      const queryRedirect = new URLSearchParams(location.search).get('redirect');

      return sanitizeRedirectPath(stateRedirect ?? queryRedirect);
    },
    [location.search, location.state]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await authRepository.login({
        email: formData.email,
        password: formData.password,
      });

      // Login bem-sucedido - agora response tem access_token e user
      if (response.access_token && response.user) {
        navigate(redirectTo, { replace: true });
      }
    } catch (error: any) {
      setError(error.message || 'Erro interno do sistema. Tente novamente.');
      console.error('Erro durante o login:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex">
      {/* Lado Esquerdo - Fundo Primário */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary/90 to-primary/80 relative overflow-hidden">
        {/* Elementos decorativos de fundo */}
        <div className="absolute top-0 left-0 w-full h-full">
          {/* Círculos decorativos */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-16 w-24 h-24 bg-white/15 rounded-full"></div>
          <div className="absolute bottom-32 left-32 w-20 h-20 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-40 w-16 h-16 bg-white/20 rounded-full"></div>
          
          {/* Formas geométricas */}
          <div className="absolute top-1/4 right-1/4 w-8 h-8 bg-white/20 transform rotate-45"></div>
          <div className="absolute bottom-1/3 left-1/4 w-6 h-6 bg-white/15 transform rotate-45"></div>
          
          {/* Linhas decorativas */}
          <div className="absolute top-1/2 left-0 w-1/3 h-px bg-white/20"></div>
          <div className="absolute bottom-1/3 right-0 w-1/4 h-px bg-white/15"></div>
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center px-12 py-16 text-textPrimary w-full">
          <div className="max-w-lg text-center space-y-12">
            {/* Logo e branding */}
            <div className="space-y-4">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-background/30 rounded-full mb-6">
                <i className="ri-home-4-line text-3xl text-background"></i>
              </div>
              <div className="flex justify-center mb-6">
                <img 
                  src="/img/habita4u.svg" 
                  alt="Habita4u" 
                  className="h-16 w-auto"
                />
              </div>
              <p className="text-xl font-instrument text-textSecondary max-w-md mx-auto">
                Bem-vindo de volta ao <span className="text-background font-fraunces font-semibold">Habita4u</span>! Estávamos à sua espera
              </p>
            </div>
            
            {/* Estatísticas */}
            <div className="grid grid-cols-3 gap-6 py-8">
              <div className="text-center">
                <div className="text-3xl font-bold mb-1 text-background">500+</div>
                <div className="text-sm text-textSecondary">Imóveis</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1 text-background">1000+</div>
                <div className="text-sm text-textSecondary">Clientes</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-1 text-background">98%</div>
                <div className="text-sm text-textSecondary">Satisfação</div>
              </div>
            </div>

            {/* Benefícios */}
            <div className="space-y-6 bg-textPrimary/10 backdrop-blur-sm rounded-2xl p-8">
              <h3 className="text-2xl font-fraunces font-semibold mb-6 text-textPrimary">
                Continue sua jornada conosco
              </h3>
              <div className="space-y-4 text-left">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-textPrimary/25 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-lg text-textPrimary"></i>
                  </div>
                  <div>
                    <div className="font-instrument font-semibold text-textPrimary">Acesso Rápido</div>
                    <div className="text-sm text-textPrimary/70">Retome suas buscas de imóveis de onde parou</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-textPrimary/25 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-lg text-textPrimary"></i>
                  </div>
                  <div>
                    <div className="font-instrument font-semibold text-textPrimary">Dados Seguros</div>
                    <div className="text-sm text-textPrimary/70">Suas informações estão protegidas e seguras</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-textPrimary/25 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-lg text-textPrimary"></i>
                  </div>
                  <div>
                    <div className="font-instrument font-semibold text-textPrimary">Suporte Personalizado</div>
                    <div className="text-sm text-textPrimary/70">Nossa equipe está pronta para ajudá-lo</div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-textPrimary/25 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-textPrimary"></i>
                  </div>
                  <div>
                    <div className="font-instrument font-semibold text-textPrimary">Atualizações em Tempo Real</div>
                    <div className="text-sm text-textPrimary/70">Receba notificações sobre novos imóveis</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Lado Direito - Formulário de Login */}
      <div className="w-full lg:w-1/2 bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-2xl font-fraunces font-semibold text-textPrimary mb-2">
              Acesse sua conta
            </h2>
            <p className="font-instrument text-textSecondary">
              Entre com seus dados para continuar
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 font-instrument">{error}</p>
              </div>
            )}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                  E-mail
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-textSecondary"></i>
                    </div>
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                    placeholder="Digite seu e-mail"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                  Senha
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-lock-line text-textSecondary"></i>
                    </div>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`ri-eye${showPassword ? '-off' : ''}-line text-textSecondary hover:text-primary`}></i>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm font-instrument text-textSecondary cursor-pointer">
                    Lembrar de mim
                  </label>
                </div>

                <div className="text-sm">
                  <a href="#" className="font-instrument font-medium text-primary hover:text-primary/80 cursor-pointer">
                    Esqueceu a senha?
                  </a>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-primary-foreground py-3 px-4 rounded-lg font-instrument font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                    <span>Entrando...</span>
                  </div>
                ) : (
                  'Entrar'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="font-instrument text-textSecondary">
                Não tem uma conta?{' '}
                <Link to="/dashboard" className="font-medium text-primary hover:text-primary/80 cursor-pointer">
                  Criar conta
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
